import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { useStatus } from '../context/StatusContext';
import { CONTACT_EMAIL, HOSTING_URL, LINKEDIN_URL, mailto } from '../lib/site';

const linkCls =
  'inline-flex items-center min-h-[36px] sm:min-h-0 text-secondary-400 hover:text-primary-300 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950';

const GROUPS: { title: string; links: { to: string; label: string }[] }[] = [
  {
    title: 'FairCorp',
    links: [
      { to: '/', label: 'Home' },
      { to: '/services', label: 'Services' },
      { to: '/work', label: 'Our Work' },
      { to: '/about', label: 'About' },
    ],
  },
  {
    title: 'Get in touch',
    links: [
      { to: '/contact', label: 'Contact' },
      { to: '/apply/partnership', label: 'Partnerships' },
      { to: '/apply/position', label: 'Careers' },
      { to: '/ticket', label: 'Support ticket' },
    ],
  },
];

const Footer: React.FC = () => {
  const { publicHubStatusEnabled, outagePageActive } = useStatus();
  return (
  <footer className="bg-secondary-950/80 backdrop-blur-sm border-t border-secondary-800/55 pt-8 pb-[max(1.25rem,env(safe-area-inset-bottom))] mt-auto">
    <div className="page-inner">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 text-sm">
        <div className="flex flex-col items-start sm:col-span-2 md:col-span-1">
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            aria-label="FairCorp home"
          >
            <img src="/FinalLogoRebuild.png" alt="" className="w-8 h-8 object-contain" />
            <span className="font-semibold text-white">FairCorp Hub</span>
          </Link>
          <p className="hidden sm:block text-secondary-500 mt-2 leading-snug">Technology for the future.</p>
          <a href={mailto()} className={`${linkCls} mt-1 break-all`}>
            {CONTACT_EMAIL}
          </a>
        </div>

        {GROUPS.map((g) => (
          <nav key={g.title} aria-label={g.title}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1.5">{g.title}</h2>
            <ul className="space-y-0.5">
              {g.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className={linkCls}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}

        <nav aria-label="More" className="sm:col-span-2 md:col-span-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-secondary-500 mb-1.5">More</h2>
          <ul className="space-y-0.5">
            <li><a href={HOSTING_URL} className={linkCls}>Hosting site ↗</a></li>
            {publicHubStatusEnabled && <li><Link to="/status" className={linkCls}>Status</Link></li>}
            {publicHubStatusEnabled && outagePageActive && <li><Link to="/outage" className={linkCls}>Outage info</Link></li>}
            <li><Link to="/tos" className={linkCls}>Terms</Link></li>
            <li><Link to="/privacy" className={linkCls}>Privacy</Link></li>
            <li>
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className={`${linkCls} gap-1.5`} aria-label="FairCorp on LinkedIn (opens in a new tab)">
                <Linkedin className="w-3.5 h-3.5" aria-hidden="true" />
                LinkedIn
              </a>
            </li>
          </ul>
        </nav>
      </div>
      <div className="gline mt-4 mb-3 sm:mt-6 sm:mb-4" />
      <p className="text-secondary-600 text-xs">&copy; {new Date().getFullYear()} FairCorp. All rights reserved.</p>
    </div>
  </footer>
  );
};

export default Footer;
